import { Model } from '../core/model';
import type { ModelJSON, GroupData } from '../core/model';
import type { Vec3 } from '../core/vec';
import type { EntityOps } from '../../server/protocol';

/**
 * Pure Sync-Primitiven für die Kollaboration: Diff zweier Modellstände und
 * Anwenden von Entity-Operationen — einmal auf ModelJSON (Server, Shadow-Kopie),
 * einmal direkt auf dem Model (Client). Bewusst ohne DOM/Netzwerk, damit alles
 * unit-testbar ist.
 */

export function emptyOps(): EntityOps {
  return {
    vertices: [], edges: [], faces: [], guides: [], groups: [],
    deletes: { vertices: [], edges: [], faces: [], guides: [], groups: [] }
  };
}

export function opsIsEmpty(ops: EntityOps): boolean {
  return ops.vertices.length === 0 && ops.edges.length === 0 && ops.faces.length === 0 &&
    ops.guides.length === 0 && ops.groups.length === 0 &&
    ops.deletes.vertices.length === 0 && ops.deletes.edges.length === 0 &&
    ops.deletes.faces.length === 0 && ops.deletes.guides.length === 0 && ops.deletes.groups.length === 0;
}

type Tupel = [number, ...unknown[]];

function sameTuple(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function byId<T extends Tupel>(list: T[]): Map<number, string> {
  const map = new Map<number, string>();
  for (const t of list) map.set(t[0], JSON.stringify(t));
  return map;
}

/**
 * Entity-Level-Diff base → cur: geänderte/neue Entities als Upserts, fehlende als Deletes.
 * Konfliktstrategie ist Last-Writer-Wins pro Entity (in Server-Ankunftsreihenfolge).
 */
export function diffModels(base: ModelJSON, cur: ModelJSON): EntityOps {
  const ops = emptyOps();
  const diff = <T extends Tupel>(b: T[], c: T[], up: T[], del: number[]): void => {
    const bm = byId(b);
    const curIds = new Set<number>();
    for (const t of c) {
      curIds.add(t[0]);
      if (bm.get(t[0]) !== JSON.stringify(t)) up.push(t);
    }
    for (const t of b) if (!curIds.has(t[0])) del.push(t[0]);
  };
  diff(base.vertices, cur.vertices, ops.vertices, ops.deletes.vertices);
  diff(base.edges, cur.edges, ops.edges, ops.deletes.edges);
  diff(base.faces, cur.faces, ops.faces, ops.deletes.faces);
  diff(base.guides, cur.guides, ops.guides, ops.deletes.guides);
  diff(base.groups ?? [], cur.groups ?? [], ops.groups, ops.deletes.groups);
  return ops;
}

/** Wendet Upserts/Deletes direkt auf ein ModelJSON an (Server-Dokument, Shadow-Kopie). */
export function applyOpsToJson(doc: ModelJSON, ops: EntityOps): void {
  const upsert = <T extends Tupel>(list: T[], ups: T[]): void => {
    const idx = new Map<number, number>();
    list.forEach((t, i) => idx.set(t[0], i));
    for (const t of ups) {
      const i = idx.get(t[0]);
      if (i === undefined) {
        idx.set(t[0], list.length);
        list.push(t);
      } else {
        list[i] = t;
      }
    }
  };
  const remove = <T extends Tupel>(list: T[], ids: number[]): void => {
    if (ids.length === 0) return;
    const del = new Set(ids);
    const kept = list.filter((t) => !del.has(t[0]));
    list.length = 0;
    list.push(...kept);
  };
  upsert(doc.vertices, ops.vertices);
  upsert(doc.edges, ops.edges);
  upsert(doc.faces, ops.faces);
  upsert(doc.guides, ops.guides);
  if (!doc.groups) doc.groups = [];
  upsert(doc.groups, ops.groups);
  remove(doc.guides, ops.deletes.guides);
  remove(doc.groups, ops.deletes.groups);
  remove(doc.faces, ops.deletes.faces);
  remove(doc.edges, ops.deletes.edges);
  remove(doc.vertices, ops.deletes.vertices);
}

/**
 * Wendet Remote-Ops auf das lokale Model an. Bestehende Entities werden **in place**
 * aktualisiert (nicht das Map-Entry ersetzt) — Werkzeuge halten während eines Drags
 * rohe Referenzen auf VertexData, die erhalten bleiben müssen.
 */
export function applyOpsToModel(model: Model, ops: EntityOps): void {
  for (const [id, p] of ops.vertices) {
    const v = model.vertices.get(id);
    // Neu anlegen statt Felder mutieren: Vec3 ist read-only, und die VertexData-
    // Referenz (die Werkzeuge während eines Drags halten) bleibt erhalten.
    if (v) v.p = { x: p[0]!, y: p[1]!, z: p[2]! };
    else model.vertices.set(id, { id, p: { x: p[0]!, y: p[1]!, z: p[2]! } });
  }
  for (const [id, a, b, group] of ops.edges) {
    const e = model.edges.get(id);
    if (e) {
      e.a = a;
      e.b = b;
      e.group = group ?? undefined;
    } else {
      model.edges.set(id, { id, a, b, group: group ?? undefined });
    }
  }
  for (const [id, outer, holes, color, group] of ops.faces) {
    const f = model.faces.get(id);
    if (f) {
      f.outer = outer;
      f.holes = holes;
      f.color = color;
      f.group = group ?? undefined;
    } else {
      model.faces.set(id, { id, outer, holes, color, group: group ?? undefined });
    }
  }
  for (const [id, a, b, infinite] of ops.guides) {
    const g = model.guides.get(id);
    const next = { id, a: { x: a[0]!, y: a[1]!, z: a[2]! }, b: { x: b[0]!, y: b[1]!, z: b[2]! }, infinite: infinite ? true : undefined };
    if (g) Object.assign(g, next);
    else model.guides.set(id, next);
  }
  for (const [id, parent, name, axes, layers] of ops.groups) {
    const g = model.groups.get(id);
    const next: GroupData = {
      id,
      parent: parent ?? undefined,
      name: name ?? undefined,
      axes: axes ? (axes.map((a) => ({ x: a[0]!, y: a[1]!, z: a[2]! })) as [Vec3, Vec3, Vec3]) : undefined,
      visible: layers ? layers[0] !== 0 : undefined,
      opacity: layers ? layers[1] : undefined
    };
    if (g) Object.assign(g, next);
    else model.groups.set(id, next);
  }

  // Deletes: erst Gruppen/Hilfslinien/Flächen/Kanten, dann Vertices
  for (const id of ops.deletes.groups) model.groups.delete(id);
  for (const id of ops.deletes.guides) model.guides.delete(id);
  for (const id of ops.deletes.faces) model.faces.delete(id);
  for (const id of ops.deletes.edges) model.edges.delete(id);
  for (const id of ops.deletes.vertices) model.vertices.delete(id);
  sanitizeAfterRemoteOps(model);
}

/** Sicherheitsnetz: hängende Kanten/Flächen entfernen, Gruppen-Markierungen ergänzen. */
function sanitizeAfterRemoteOps(model: Model): void {
  for (const id of [...model.edges.keys()]) {
    const e = model.edges.get(id)!;
    if (!model.vertices.has(e.a) || !model.vertices.has(e.b)) model.edges.delete(id);
  }
  for (const id of [...model.faces.keys()]) {
    const f = model.faces.get(id)!;
    const missing = f.outer.some((v) => !model.vertices.has(v)) || f.holes.some((h) => h.some((v) => !model.vertices.has(v)));
    if (missing) model.faces.delete(id);
  }
  // Ältere Dateien/ops können Gruppen nur als Markierung an Kanten/Flächen tragen
  for (const ent of [...model.edges.values(), ...model.faces.values()]) {
    if (ent.group !== undefined && !model.groups.has(ent.group)) model.groups.set(ent.group, { id: ent.group });
  }
}

/**
 * Setzt nextId auf das ID-Band des Slots: Dadurch erzeugen parallele Teilnehmer
 * disjunkte Entity-IDs, ohne das Tupelformat oder das Picking zu ändern.
 * Slot 0 (lokaler Modus) lässt nextId unangetastet.
 */
export function applyIdBand(model: Model, slot: number): void {
  const lo = slot * (1 << 20);
  const hi = lo + (1 << 20);
  let high = lo;
  const scan = (ids: Iterable<number>): void => {
    for (const id of ids) if (id >= lo && id < hi && id > high) high = id;
  };
  scan(model.vertices.keys());
  scan(model.edges.keys());
  scan(model.faces.keys());
  scan(model.guides.keys());
  scan(model.groups.keys());
  model.nextId = Math.max(model.nextId, high + 1, lo + 1);
}
